import React from "react";
import { ProgressSpinner } from "primereact/progressspinner";
import { Column } from "primereact/column";
import { Row } from "primereact/row";
import {
  PRIMARY,
  type TableDataRow,
} from "app/pages/InteractiveReviewCenter/pages/performanceChartModel";
import InsuredTable from "app/uicomponents/macrocomponents/InteractiveProposal/Funding/InsuredTable";
import { type PerformanceTableColumnDefinition } from "./PerofmanceTable";

const IRC_PERF_GROUPS: [string, number][] = [
  ["Plan Projections", 8], // illustratedPlanAccumulatedValue, actualAccountValue, illustratedPlanSurrenderValue, actualSurrenderValue, illustratedPlanDB, actualDeathBenefit, estimatedLoanPayoffOrIncome, totalIncome
  ["Financing Info", 4], // eoyOutstandingLoanBalance, actualFinancingBalance, interestRate, interestCharge
  ["Financed Payments", 1], // lenderPremium
  ["", 1], // outsideCollateralRequirement
  ["", 1], // interestCredits
];

export interface PerformanceTablePanelProps {
  isDark: boolean;
  cardBg: string;
  textSub: string;
  chartBusy: boolean;
  columns: PerformanceTableColumnDefinition[];
  rows: TableDataRow[];
  rowClassName: (rowData: TableDataRow) => string;
  activeValueFilters?: Set<string>;
}

const PerformanceTablePanel = React.memo((props: PerformanceTablePanelProps) => {
  const { isDark, cardBg, textSub, chartBusy, columns, rows, rowClassName, activeValueFilters } =
    props;

  const grayColor = isDark ? "#6B7280" : "#9CA3AF";
  const processedColumns = React.useMemo(() => {
    if (!activeValueFilters) return columns;
    return columns.map((col) => {
      if (!col.metricId || activeValueFilters.has(col.metricId)) return col;
      return {
        ...col,
        header: (<span style={{ color: grayColor, opacity: 0.6 }}>{col.header}</span>) as any,
        style: { ...col.style, color: grayColor, opacity: 0.45 },
      };
    });
  }, [columns, activeValueFilters, grayColor]);

  const headerGroupRow = (
    <Row>
      <Column
        header=""
        rowSpan={1}
        colSpan={3}
        style={{ background: isDark ? "#3A3A50" : "#EBEBEF" }}
        align="center"
      />
      {IRC_PERF_GROUPS.map(([name, span], i) => (
        <Column
          key={`${name || "__gap__"}-${i}`}
          header={name}
          rowSpan={1}
          colSpan={span}
          style={{ background: isDark ? "#3A3A50" : "#EBEBEF" }}
          align="center"
        />
      ))}
    </Row>
  );

  return (
    <div
      className={`rounded-2xl overflow-hidden border shadow-sm w-full min-w-0 max-w-full ${
        isDark ? "border-[#3A3A50] shadow-none" : "border-slate-200/90 shadow-md"
      } ${cardBg}`}
    >
      {!chartBusy && (
        <div className="w-full min-w-0 max-w-full px-0 pb-4 pt-0">
          {rows.length ? (
            <InsuredTable
              columns={processedColumns}
              data={rows}
              hoverColor={PRIMARY}
              columnGroup={headerGroupRow}
              scrollable
              scrollHeight="600px"
              rowClassName={rowClassName}
            />
          ) : (
            <p className={`text-center text-sm py-4 ${textSub}`}>No rows to display.</p>
          )}
        </div>
      )}
      {chartBusy && (
        <div className="flex justify-center py-8">
          <ProgressSpinner style={{ width: 42, height: 42 }} />
        </div>
      )}
    </div>
  );
});

PerformanceTablePanel.displayName = "PerformanceTablePanel";

export default PerformanceTablePanel;
